import {FC, MouseEvent, useState} from 'react';
import {ReceiptLongOutlined} from '@mui/icons-material';
import {Box, Button, Popover, Typography} from '@mui/material';
import {useIntl} from 'react-intl';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {ActionSheet, ActionSheetDestructive} from 'src/components/mobile/ActionSheet';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {phonePillsRowSx} from 'src/components/mobile/mobileDesign.sx';
import {TableCard} from 'src/components/tables/respnsiveTable/TableCard';
import {MenuButton} from 'src/components/tables/respnsiveTable/MenuButton';
import {CustomInvoiceItemCardProps} from '../CustomInvoiceItemsTable.types';
import {
    DescriptionCell,
    InvoiceCell,
    isItemLocked,
    PriceCell,
    QuantityCell,
    StatusCell,
} from '../CustomInvoiceItemsTable.tableConfig';
import {menuBtnSx, menuBtnTextSx, menuContentSx, popoverSx} from './CustomInvoiceItemCard.sx';

export const CustomInvoiceItemCard: FC<CustomInvoiceItemCardProps> = ({item, onEdit, onDelete}) => {
    const intl = useIntl();
    // Below a desktop the menu is a sheet; from md up it stays the popover it has always been.
    const {isCompact} = useScreenTier();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);

    // The same rule the desktop row follows: an item already placed on an invoice is read-only.
    const locked = isItemLocked(item);
    const hasActions = !!onEdit || !!onDelete;

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        handleClose();
        onEdit?.(item);
    };

    const handleDelete = () => {
        handleClose();
        onDelete?.(item.id);
    };

    // The same two nodes for both widths. Split only so the sheet can put the destructive one below
    // a rule; the popover still renders them in the one column it always did.
    const editAction = onEdit ? (
        <Button
            sx={menuBtnSx}
            disabled={locked}
            onClick={handleEdit}
        >
            <Typography sx={menuBtnTextSx}>{intl.formatMessage({id: 'edit'})}</Typography>
        </Button>
    ) : null;

    const deleteAction = onDelete ? (
        <Button
            sx={menuBtnSx}
            disabled={locked}
            onClick={handleDelete}
        >
            <Typography sx={menuBtnTextSx}>{intl.formatMessage({id: 'delete'})}</Typography>
        </Button>
    ) : null;

    // The menu and the overlay it opens travel together into the head's action slot: the popover
    // anchors on the button's element, not on where it sits in the card.
    const menuAction = hasActions ? (
        <>
            <MenuButton handleClick={handleClick}/>
            {isCompact ? (
                <ActionSheet
                    open={open}
                    onClose={handleClose}
                    icon={<ReceiptLongOutlined/>}
                    title={<DescriptionCell row={item}/>}
                >
                    {editAction}
                    <ActionSheetDestructive>{deleteAction}</ActionSheetDestructive>
                </ActionSheet>
            ) : (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    sx={popoverSx}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <Box sx={menuContentSx}>
                        {editAction}
                        {deleteAction}
                    </Box>
                </Popover>
            )}
        </>
    ) : null;

    return (
        <TableCard>
            <PhoneCardHead
                icon={<ReceiptLongOutlined/>}
                title={<DescriptionCell row={item}/>}
                action={menuAction}
            />
            <Box sx={phonePillsRowSx}>
                <StatusCell row={item}/>
            </Box>
            <PhoneCardFacts
                facts={[
                    {
                        label: intl.formatMessage({id: 'quantity'}),
                        value: <QuantityCell row={item}/>,
                    },
                    {
                        label: intl.formatMessage({id: 'priceInclVat'}),
                        value: <PriceCell row={item}/>,
                    },
                    {
                        label: intl.formatMessage({id: 'invoice'}),
                        value: <InvoiceCell row={item}/>,
                    },
                ]}
            />
        </TableCard>
    );
};
